import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import calculatePosition from './calculatePosition';
import './Banner.scss';
import classnames from 'classnames';
import { useDebouncedCallback } from 'use-debounce';
import { RawHTML } from '../../../core/components';
function getScrollParent(node) {
    if (node == null) {
        return null;
    }
    return node.scrollHeight > node.clientHeight || node.tagName === 'BODY'
        ? node
        : getScrollParent(node.parentNode);
}
export default function Banner({ className, anchorId, placement, title = '', description, onClose, margin = 4, portalContainer = document.body, }) {
    const bannerRef = useRef(null);
    const [bannerPosition, setBannerPosition] = useState({ x: 0, y: 0 });
    const [arrowPosition, setArrowPosition] = useState({ x: 0, y: 0 });
    const debUpdate = useDebouncedCallback((cb) => {
        cb();
    }, 500);
    useEffect(() => {
        const banner = bannerRef.current;
        const anchor = document.querySelector(`#${anchorId}`);
        const mainUiView = document.querySelector('#main-ui-view');
        let scrollableContainer = getScrollParent(portalContainer);
        function setPosition() {
            const { popup, arrow } = calculatePosition(banner.getBoundingClientRect(), anchor.getBoundingClientRect(), placement, margin);
            setBannerPosition(popup);
            setArrowPosition(arrow);
        }
        function updateScrollableContainer() {
            const newContainer = getScrollParent(portalContainer);
            if (newContainer === scrollableContainer) {
                return;
            }
            scrollableContainer.removeEventListener('scroll', setPosition);
            scrollableContainer = newContainer;
            scrollableContainer.addEventListener('scroll', setPosition);
        }
        updateScrollableContainer();
        window.addEventListener('resize', () => {
            updateScrollableContainer();
            setPosition();
        });
        const resizeObserver = new ResizeObserver(setPosition);
        resizeObserver.observe(anchor.parentElement);
        let isSidebarHiddenOldVal = null;
        const uiViewMutableObserver = new MutationObserver(() => {
            const isHiddenNow = mainUiView.classList.value.includes('sidebar-hidden');
            if (isSidebarHiddenOldVal && !isHiddenNow) {
                debUpdate(setPosition);
            }
            isSidebarHiddenOldVal = isHiddenNow;
        });
        uiViewMutableObserver.observe(mainUiView, {
            attributes: true,
            attributeOldValue: true,
            attributeFilter: ['class'],
        });
        return () => {
            window.removeEventListener('resize', setPosition);
            resizeObserver.disconnect();
            scrollableContainer.removeEventListener('scroll', setPosition);
            uiViewMutableObserver.disconnect();
        };
    }, [anchorId, margin, placement]);
    const getMessage = useCallback((message) => {
        return typeof message === 'string' ? <RawHTML>{message}</RawHTML> : message;
    }, []);
    return createPortal(<div className={classnames('banner', className)} style={{ left: bannerPosition.x, top: bannerPosition.y }} ref={bannerRef}>
      <div className="banner__content">
        <div className="banner__header">
          <div className={title ? 'banner__title' : 'banner__body'}>
            {getMessage(title || description)}
          </div>
          <button className="banner__close-button" onClick={onClose}>
            <svg className="banner__close-button__icon">
              <use xlinkHref="#call-settings-close"></use>
            </svg>
          </button>
        </div>
        {title ? (<div className="banner__body">{getMessage(description)}</div>) : ('')}
      </div>
      <div className="banner__arrow" style={{ left: arrowPosition.x, top: arrowPosition.y }}/>
    </div>, portalContainer);
}
